import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { paymentService } from 'api/payment';
import { logoutUser } from './authSlice';

export interface Network {
  id: number;
  network: string;
  name: string;
  requiresTag: boolean;
  minAmountUsd: string;
  providerMinAmount: string;
  isFavorite?: boolean;
}

export interface PaymentMethod {
  id: number;
  paymentSettingId: number;
  externalId: number;
  code: string;
  name: string;
  imageUrl: string | null;
  minAmount?: string | number | null;
  minAmountUsd?: string | number | null;
  networks?: Network[];
}

export interface AddressData {
  userDepositAddressId: number;
  address: string;
  cryptoCurrency: string;
  isNew: boolean;
  tag?: string;
  requiresTag?: boolean;
  network?: string;
  networkName?: string;
}

interface PaymentState {
  depositMethods: PaymentMethod[];
  withdrawMethods: PaymentMethod[];
  selectedMethod: PaymentMethod | null;
  selectedWithdrawMethod: PaymentMethod | null;
  addresses: Record<number, AddressData>;
  isLoading: boolean;
  isAddressLoading: boolean;
  error: string | null;
  successMessage: string | null;
  isDepositFrozen: boolean;
}

const loadAddressesFromStorage = () => {
  try {
    /*
    const data = localStorage.getItem('payment_addresses');
    return data ? JSON.parse(data) : {};
    */
    localStorage.removeItem('payment_addresses'); // Actively clean up old data for returning users
    return {};
  } catch (e) {
    return {};
  }
};

const initialState: PaymentState = {
  depositMethods: [],
  withdrawMethods: [],
  selectedMethod: null,
  selectedWithdrawMethod: null,
  addresses: loadAddressesFromStorage(),
  isLoading: false,
  isAddressLoading: false,
  error: null,
  successMessage: null,
  isDepositFrozen: false,
};

export const fetchDepositAddress = createAsyncThunk(
  'payment/fetchDepositAddress',
  async ({ methodId, networkId }: { methodId: number; networkId: number }, { rejectWithValue }) => {
    try {
      const response = await paymentService.createDepositAddress(methodId, networkId);
      return response.data;
    } catch (err: any) {
      const status = err.response?.status;
      const errorKey = err.response?.data?.error || err.response?.data?.errorCode;
      const isFrozen = status === 403 || errorKey === 'errors.payment.deposits_disabled';
      const message =
        err.response?.data?.message || err.message || 'Failed to generate deposit address';
      return rejectWithValue({ message, isFrozen, status, errorKey });
    }
  },
);

export const createWithdrawal = createAsyncThunk(
  'payment/createWithdrawal',
  async (
    data: {
      withdrawMethodId: number;
      amount: string | number;
      destinationAddress: string;
      destinationTag?: string;
      currencyCode: string;
      networkId: number;
      idempotencyKey?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await paymentService.createWithdrawal(data, data.idempotencyKey);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Withdrawal failed');
    }
  },
);

const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
      state.isDepositFrozen = false;
    },
    setSelectedMethod: (state, action) => {
      state.selectedMethod = action.payload;
      state.error = null;
      state.successMessage = null;
      state.isDepositFrozen = false;
    },
    setSelectedWithdrawMethod: (state, action) => {
      state.selectedWithdrawMethod = action.payload;
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDepositAddress.pending, (state, action) => {
        state.isAddressLoading = true;
        state.error = null;
        state.isDepositFrozen = false;
        // Clear the old address immediately to prevent a visual "flash" of old data
        if (action.meta.arg.networkId) {
          delete state.addresses[action.meta.arg.networkId];
        }
      })
      .addCase(fetchDepositAddress.fulfilled, (state, action) => {
        state.isAddressLoading = false;
        state.addresses[action.meta.arg.networkId] = action.payload;
        // localStorage.setItem('payment_addresses', JSON.stringify(state.addresses));
      })
      .addCase(fetchDepositAddress.rejected, (state, action) => {
        state.isAddressLoading = false;
        const payload: any = action.payload;
        state.isDepositFrozen = !!payload?.isFrozen;
        state.error =
          typeof payload === 'string'
            ? payload
            : payload?.message || 'Failed to generate deposit address';
      })
      .addCase(createWithdrawal.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createWithdrawal.fulfilled, (state, action) => {
        state.isLoading = false;
        state.successMessage =
          action.payload.message || 'Withdrawal request submitted successfully';
      })
      .addCase(createWithdrawal.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.successMessage = null;
      })
      .addCase(logoutUser.fulfilled, () => {
        localStorage.removeItem('payment_addresses');
        return { ...initialState, addresses: {} };
      });
  },
});

export const { clearMessages, setSelectedMethod, setSelectedWithdrawMethod } = paymentSlice.actions;
export default paymentSlice.reducer;
